import { useEffect } from 'react';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { shouldSend, windowOf, type ActivityState } from '../lib/heartbeat';

/**
 * Akhara time heartbeat. At most one POST /api/heartbeat per 5-minute window, only while
 * the tab is visible and the person is active (see lib/heartbeat.ts). The server takes
 * the window from its own clock; there is no body. Best-effort: never throws. Renders nothing.
 */
export default function Heartbeat() {
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (!uid) return;
    let lastInputAt: number | null = null;
    let lastMove = 0;
    let lastSent: number | null = null;
    let inFlight = false;

    const state = (): ActivityState => {
      let mediaActive = false;
      try {
        mediaActive =
          document.activeElement?.tagName === 'IFRAME' ||
          Array.from(document.querySelectorAll('video')).some((v) => !v.paused && !v.ended);
      } catch {
        /* ignore */
      }
      return { lastInputAt, visible: document.visibilityState === 'visible', mediaActive };
    };

    const tick = () => {
      const now = Date.now();
      if (inFlight || !shouldSend(now, state(), lastSent)) return;
      const u = auth.currentUser;
      if (!u || u.uid !== uid) return;
      const win = windowOf(now);
      inFlight = true;
      void (async () => {
        try {
          const token = await u.getIdToken();
          const res = await fetch('/api/heartbeat', {
            method: 'POST',
            keepalive: true,
            credentials: 'omit',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) lastSent = win;
        } catch {
          /* best-effort */
        } finally {
          inFlight = false;
        }
      })();
    };

    const onInput = () => {
      lastInputAt = Date.now();
      tick();
    };
    const onMove = () => {
      const now = Date.now();
      if (now - lastMove < 1000) return;
      lastMove = now;
      onInput();
    };

    const opts = { passive: true, capture: true } as const;
    const events = ['pointerdown', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, onInput, opts));
    window.addEventListener('mousemove', onMove, opts);
    document.addEventListener('visibilitychange', tick);
    // Clicks inside the Bunny player iframe never reach this window; focus moving into it does.
    const onBlur = () => {
      window.setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') onInput();
      }, 0);
    };
    window.addEventListener('blur', onBlur);
    const id = window.setInterval(tick, 60_000);
    tick();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onInput, opts));
      window.removeEventListener('mousemove', onMove, opts);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('blur', onBlur);
      window.clearInterval(id);
    };
  }, [uid]);

  return null;
}
